import type {
  ViewFilter,
  ViewFilter3DAdminOnly,
  ViewFilter3DInternals,
  ViewFilter3DDebug,
  ViewFilterFraming,
  ViewFilterKeyMeasures,
  ViewFilterProcureMeasures,
  ViewFilterVisualAids,
} from "building-types/views/viewFilterTypes";
import { camelCaseToTitleCase } from "common/utils/strings";
import { defaultRenderDebugSettings } from "lib/house/render/buildingViewerSceneState";

// These settings are not user controllable and hidden in the UI even for admin users.
// Different types of viewers might use different settings for these render stages.
export const internalViewFilter3DDefaults: ViewFilter3DInternals & ViewFilter3DDebug = {
  camera: true,
  debug: true, // Only adds things to scene if there are debug calls in the code
  debugBuildingOutlineVertices: true, // Actual visibility is determined by debug settings
  debugLevelExtrusions: true, // Actual visibility is determined by debug settings
  buildingGraph: true, // Actual visibility is determined by debug settings
  sao: true,
  sceneLights: true,
  warmup: false, // found to be detrimental to stability
  solarPotential: false, // experimental
  lut: false, // unused with the current lighting settings
  siteLightProbes: true, // controlled by scene state
  exteriorLightProbes: true, // controlled by scene state
  interiorLightProbes: true, // controlled by scene state
  roofsDebug: true, // Actual visibility is determined by debug settings
};

export const visualAidsViewFilter3DKeys: Omit<ViewFilterVisualAids, "sheet" | "issues" | "misalignedWallWarning"> &
  Pick<ViewFilter, "siteShapes"> = {
  grid: true,
  homeSideIndicator: true,
  roofSideSelection: true,
  siteShapes: true, // Site shapes is a "visual aid" category separate from the actual site in 3D but has different meaning in plan view.
  sectionViews: true, // Not currently visualized in 3D view but could be in the future.
};

export const referenceViewFilter3DKeys: Partial<ViewFilter> = {
  underlays: true,
};

export const adminOnlyViewFilter3DKeys: ViewFilter3DAdminOnly = {
  crossSection: true,
  faceDiff: true,
  propBoundingBoxes: true,
};

export const keyMeasuresViewFilter3DKeys: ViewFilterKeyMeasures = {
  analyticColumns: true,
  analyticFoundationSlabs: true,
  analyticFoundationFootings: true,
  analyticStemWalls: true,
  analyticRailings: true,
  interiorTrimAnalyticEntities: true,
  ceilingAnalyticFaces: true,
  roofAnalyticFaces: true,
  roofAnalyticEdges: true,
  analyticRoofSoffits: true,
  roofAnalyticFlashing: true,
  doorTrimAnalyticEntities: true,
  windowTrimAnalyticEntities: true,
  wallInteriorFinishAnalyticFaces: true,
  wallInteriorFinishAnalyticFacesLOD2: true,
  wallExteriorFinishAnalyticFaces: true,
  wallExteriorFinishAnalyticEdges: true,
  foundationKeyMeasures: true,
  floorKeyMeasures: true,
  porchKeyMeasures: true,
  roofKeyMeasures: true,
  wallStructuralDomains: true,
  analyticFloorStructures: true,
};

export const procureMeasuresViewFilter3DKeys: ViewFilterProcureMeasures = {
  procureMeasuresEaves: true,
  procureMeasuresFlashing: true,
  procureMeasuresRidges: true,
  procureMeasuresRidgeVents: true,
  procureMeasuresRoofAreas: true,
  procureMeasuresValleys: true,
  procureMeasuresFacadeTrim: true,
  procureMeasuresInteriorDoors: true,
  procureMeasuresExteriorDoors: true,
  procureMeasuresInteriorWindows: true,
  procureMeasuresExteriorWindows: true,
  procureMeasuresRoofTrim: true,
  procureMeasuresRoomPerimeter: true,
};

export const framingViewFilter3DKeys: ViewFilterFraming = {
  wallFraming: true,
  wallFramingRegions: false,
  gableEndWallFraming: true,
  floorFraming: true,
  floorFramingLabels: false,
  subFlooring: true,
  slabFlooring: true,
  roofFraming: false,
};

export const foundationViewFilter3DKeys: Partial<ViewFilter> = {
  slab: true,
  stemWall: true,
  foundationCurb: true,
  foundationFooting: true,
  foundationPiers: true,
};

export function cleanupKeyMeasuresViewFilterKey(id: string) {
  const suffix = "KeyMeasures";
  const strippedKey = id.endsWith(suffix) ? id.replace(suffix, "") : id;

  return camelCaseToTitleCase(strippedKey);
}

export function cleanupProcureMeasuresViewFilterKey(id: string) {
  const prefix = "procureMeasures";
  const strippedKey = id.startsWith(prefix) ? id.replace(prefix, "") : id;

  return camelCaseToTitleCase(strippedKey);
}

export const debugTableKeys = Object.keys(defaultRenderDebugSettings);
